import { z } from 'zod';
import { DEFAULT_GENERATION_MODEL_KEY, PROMPT_INJECTION_PATTERN } from '../constants.js';
import {
  GenerationModelKeySchema,
  SafeGenerationModelMetadataSchema,
  getGenerationModelMetadata,
} from './generation-models.js';
import { EntryOrFreeTextSchema } from './runs.js';

// ─── Ideation request ─────────────────────────────────────────────────────────

// Partial brief fields the writer has already filled in; each uses the same
// 3-way union as the full run request so ephemeral values are accepted here too.
const PartialBriefSchema = z.object({
  angle: EntryOrFreeTextSchema.optional(),
  avatar: EntryOrFreeTextSchema.optional(),
  problemMechanism: EntryOrFreeTextSchema.optional(),
  solutionMechanism: EntryOrFreeTextSchema.optional(),
  ingredients: z.array(EntryOrFreeTextSchema).max(20).optional(),
  awarenessLevel: EntryOrFreeTextSchema.optional(),
});

const IdeateRequestObjectSchema = z.object({
  productId: z.string().min(1),
  generationModelKey: GenerationModelKeySchema.optional(),
  market: z.string().min(2).max(80),
  direction: z
    .string()
    .max(500)
    .refine((s) => !PROMPT_INJECTION_PATTERN.test(s), 'prompt_injection_pattern')
    .optional(),
  // Server defaults to 8 when omitted.
  count: z.number().int().min(4).max(12).optional(),
  partialBrief: PartialBriefSchema.optional(),
});
export const IdeateRequestSchema = z.preprocess((input) => {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const record = input as Record<string, unknown>;

    return {
      ...record,
      ...(record.generationModelKey === undefined
        ? { generationModelKey: DEFAULT_GENERATION_MODEL_KEY }
        : {}),
    };
  }

  return input;
}, IdeateRequestObjectSchema);
export type IdeateRequest = z.infer<typeof IdeateRequestSchema>;

// ─── Single AI-generated idea card ───────────────────────────────────────────

export const ParsedIdeaSchema = z.object({
  title: z.string().min(1).max(120),
  angle: z.string().min(1).max(120),
  avatar: z.string().min(1).max(200),
  problemMechanism: z.string().min(1).max(120),
  solutionMechanism: z.string().min(1).max(120),
  // Single hierarchy-preserving string, e.g. "extra virgin olive oil polyphenols (hydroxytyrosol, oleocanthal)"
  keyIngredients: z.string().min(1).max(300),
  awarenessLevel: z.string().min(1).max(60),
  whyItWorks: z.string().min(1).max(400),
});
export type ParsedIdea = z.infer<typeof ParsedIdeaSchema>;

// ─── Ideation endpoint response ───────────────────────────────────────────────

// Cost is NOT included — writers don't see it; it lives only in the audit row.
export const IdeateResponseSchema = z.object({
  ok: z.literal(true),
  ideas: z.array(ParsedIdeaSchema),
});
export type IdeateResponse = z.infer<typeof IdeateResponseSchema>;

// ─── Ideation audit row shape ─────────────────────────────────────────────────

// Matches the IdeationRequest Prisma model added by T2.
const IdeationRequestRecordObjectSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  productId: z.string(),
  productVersion: z.number().int().positive(),
  market: z.string(),
  direction: z.string().nullable(),
  partialBrief: z.unknown().nullable(),
  ideas: z.array(ParsedIdeaSchema),
  model: z.string(),
  reasoningEffort: z.string(),
  generationModel: SafeGenerationModelMetadataSchema.optional(),
  inputTokens: z.number().int().nullable(),
  cachedInputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  reasoningTokens: z.number().int().nullable(),
  estimatedCostUsd: z.number().nullable(),
  createdAt: z.string(),
});
export const IdeationRequestRecordSchema = z.preprocess((input) => {
  if (input && typeof input === 'object' && !Array.isArray(input)) {
    const record = input as Record<string, unknown>;

    return {
      ...record,
      ...(record.generationModel === undefined
        ? { generationModel: getGenerationModelMetadata(DEFAULT_GENERATION_MODEL_KEY) }
        : {}),
    };
  }

  return input;
}, IdeationRequestRecordObjectSchema);
export type IdeationRequestRecord = z.infer<typeof IdeationRequestRecordSchema>;
